import { getStrings, localePath, studioUrl, type Lang } from '../lib/i18n';
import { exercisesBase } from '../lib/exercises';
import { hubStrings } from '../lib/tools';

export const Footer = ({ lang = 'en' as Lang }: { lang?: Lang }) => {
  const currentYear = new Date().getFullYear();
  const t = getStrings(lang).footer;
  const c = getStrings(lang).exercisesChrome;
  // Legal statics exist only in English (root) and Russian (/ru); other locales fall back to English.
  const legalBase = lang === 'ru' ? '/ru' : '';

  return (
    <footer className="bg-elevated border-t-2 border-line py-10">
      <div className="container mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <img src="/logo.png" alt="ArmProgress Logo" width={40} height={40} className="w-10 h-10 rounded-xl" />
            <span className="font-heading text-ink text-xl font-black tracking-tight">ArmProgress<span className="text-primary">.</span></span>
          </div>

          {/* Social Links */}
          <div className="flex gap-2.5">
            <a href="https://www.tiktok.com/@armprogress3" target="_blank" rel="noopener noreferrer" className="btn-3d btn-3d--ghost w-10 h-10 p-0 text-muted hover:text-[var(--brand-ink)]" aria-label="TikTok">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a href="https://instagram.com/armprogress" target="_blank" rel="noopener noreferrer" className="btn-3d btn-3d--ghost w-10 h-10 p-0 text-muted hover:text-[var(--brand-ink)]" aria-label="Instagram">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37zm1.5-4.87h.01" />
              </svg>
            </a>
            <a href="https://www.youtube.com/@ArmProgressApp" target="_blank" rel="noopener noreferrer" className="btn-3d btn-3d--ghost w-10 h-10 p-0 text-muted hover:text-[var(--brand-ink)]" aria-label="YouTube">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/people/ArmProgress/61585853075161/" target="_blank" rel="noopener noreferrer" className="btn-3d btn-3d--ghost w-10 h-10 p-0 text-muted hover:text-[var(--brand-ink)]" aria-label="Facebook">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
              </svg>
            </a>
            <a href="https://t.me/armprogress" target="_blank" rel="noopener noreferrer" className="btn-3d btn-3d--ghost w-10 h-10 p-0 text-muted hover:text-[var(--brand-ink)]" aria-label="Telegram">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
              </svg>
            </a>
          </div>

          {/* Links */}
          <div className="flex flex-col lg:flex-row items-center gap-4 text-sm">
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:gap-x-4">
              <a href={localePath(lang, '/blog')} className="font-semibold text-muted hover:text-[var(--brand-ink)] transition-colors">
                {t.blog}
              </a>
              <span className="hidden sm:inline text-faint" aria-hidden="true">·</span>
              <a href={exercisesBase(lang)} className="font-semibold text-muted hover:text-[var(--brand-ink)] transition-colors">
                {c.navLabel}
              </a>
              <span className="hidden sm:inline text-faint" aria-hidden="true">·</span>
              <a href={localePath(lang, '/tools')} className="font-semibold text-muted hover:text-[var(--brand-ink)] transition-colors">
                {hubStrings(lang).navLabel}
              </a>
              <span className="hidden sm:inline text-faint" aria-hidden="true">·</span>
              <a href={`${legalBase}/contact.html`} className="font-semibold text-muted hover:text-[var(--brand-ink)] transition-colors">
                {t.contact}
              </a>
              <span className="hidden sm:inline text-faint" aria-hidden="true">·</span>
              <a href={`${legalBase}/privacy-policy.html`} target="_blank" rel="noopener noreferrer" className="font-semibold text-muted hover:text-[var(--brand-ink)] transition-colors">
                {t.privacy}
              </a>
              <span className="hidden sm:inline text-faint" aria-hidden="true">·</span>
              <a href={`${legalBase}/terms-and-conditions.html`} target="_blank" rel="noopener noreferrer" className="font-semibold text-muted hover:text-[var(--brand-ink)] transition-colors">
                {t.terms}
              </a>
            </div>
            <a href="https://buy.stripe.com/fZu4gzfny8dr7zwfd587K00" target="_blank" rel="noopener noreferrer" className="btn-3d btn-3d--sm px-5 py-2.5">
              {t.support}
            </a>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t-2 border-line text-center">
          <p className="text-faint text-sm">
            © {currentYear} ArmProgress. {t.rights}
          </p>
          <p className="text-faint text-sm mt-1">
            {t.madeBy}{' '}
            <a href={studioUrl(lang)} title="MCDevLabs — app development studio" className="font-semibold text-muted hover:text-[var(--brand-ink)] transition-colors">
              MCDevLabs
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
